import * as React from "react";
import {
    Mail20Regular,
    CalendarLtr20Regular,
    ClipboardTaskListLtr20Regular,
    Document20Regular,
    Dismiss20Regular,
    ChevronLeft20Regular,
} from "@fluentui/react-icons";
import type { Email } from "../../services/graphMailService";
import { AUB, INK2, LINE, BG_SOFT, GOLD, ACTIVE_BG } from "../chat/chatTheme";
import { sichtbareTabs, type SidebarTabKey } from "./sidebar/sidebarTabs";
import type { Zuweisungsquelle } from "./mailAssignedContact";
import { SchriftwechselTab } from "./sidebar/SchriftwechselTab";
import { TermineTab } from "./sidebar/TermineTab";
import { VorgaengeTab } from "./sidebar/VorgaengeTab";
import { DokumenteTab } from "./sidebar/DokumenteTab";
import type { AblageEingabe, AnhangEingabe } from "./sidebar/dokumenteGruppen";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * Die rechte Leiste der geöffneten Mail — HÜLLE.
 *
 * Eine sehr schmale, vertikal beschriftete Klickleiste am rechten Rand; ein
 * Reiter schiebt ein Overlay von rechts über den Mailtext. Die Hülle hält nur
 * Rail, Overlay, Reiterkopf und ESC/Außenklick — die Inhalte liegen in
 * `mail/sidebar/*Tab.tsx` und bringen ihren eigenen Lade-/Fehlerzustand mit.
 * Vorher teilten sich Schriftwechsel, Termine und Vorgänge einen einzigen
 * Zustand, wodurch ein Fehler in einem Reiter in allen anderen erschien.
 *
 * Der Pfad dieser Datei bleibt bewusst unverändert, damit parallel laufende
 * Cluster ihre Änderungen nicht in eine verschobene Datei schreiben.
 */

// J1 — die Leiste gibt es jetzt auch im Fenster, das schmal gezogen werden
// kann: 36 px Rail statt 40, und das Overlay lässt die Rail immer frei.
const RAIL_W = 36;
const PANEL_W = 360;

const TAB_ICONS: Record<SidebarTabKey, React.ReactNode> = {
    schriftwechsel: <Mail20Regular />,
    termine: <CalendarLtr20Regular />,
    vorgaenge: <ClipboardTaskListLtr20Regular />,
    dokumente: <Document20Regular />,
};

export type MailContactSidebarProps = {
    /** Zugeordneter KMVS-Kontakt der geöffneten Mail. Optional — Schriftwechsel
     *  und Termine funktionieren auch ohne (nur über `mailAddresses`). */
    contact?: { id: number; displayName: string } | null;
    /**
     * J2 — woher die Zuweisung stammt. Steuert die Kopfzeile und (im
     * Schriftwechsel) die Adressstrategie: `keiner` heißt „keine Zuweisung",
     * dann bleibt es beim adressbasierten Verlauf.
     */
    contactQuelle?: Zuweisungsquelle;
    /** Alle lesbaren Postfächer — Schriftwechsel sucht über alle (Entscheidung 21). */
    readableMailboxes: string[];
    /** Adresse(n) der geöffneten Mail (i. d. R. der Absender). */
    mailAddresses?: string[];
    /** Postfach der geöffneten Mail — zusammen mit `email.id` adressiert es die
     *  Graph-Mail für den Kommentar-Reiter. Ohne beides entfällt der Reiter. */
    mailbox?: string | null;
    /** Die geöffnete Mail. */
    email?: Email | null;
    /**
     * J1 — kontrolliert (Fenster-Modus, der Reiter lebt im Fensterrahmen und
     * überlebt die In-Fenster-Navigation) oder `undefined` ⇒ interner Zustand
     * (Lesepanel, dort remountet nichts, was ihn wegwerfen würde).
     */
    aktiverReiter?: SidebarTabKey | null;
    onReiterWechsel?: (t: SidebarTabKey | null) => void;
    /** Klick auf einen Schriftwechsel-Treffer. Ohne Callback nicht klickbar. */
    onOpenMail?: (mailbox: string, email: Email) => void;
    /** J1 — Zurück-Pfeil der In-Fenster-Navigation (nur im Fenster-Modus). */
    onNavBack?: () => void;
    /** J6 — Anhänge der offenen Mail, durchgereicht (kein zweiter Ladepfad). */
    anhaenge?: readonly AnhangEingabe[];
    /** J6 — schon beim Kontakt gespeicherte Anhänge (`messageId:attachmentId`). */
    gespeicherteAnhangKeys?: ReadonlySet<string>;
    onOpenAnhang?: (a: AnhangEingabe) => void;
    onDownloadAnhang?: (a: AnhangEingabe) => void;
    onSaveAnhang?: (a: AnhangEingabe) => void;
    /** J6 — die dauerhafte Ablage dieser Mail (Cluster ABLAGE); ohne sie entfällt die Gruppe. */
    ablage?: readonly AblageEingabe[] | null;
    /**
     * A11 — die Leiste hat auf eine Seite darunter navigiert (z. B. einen
     * Vorgang). Im Fenster-Modus minimiert der Aufrufer das Mail-Fenster,
     * sonst verdeckte es genau das, was der Nutzer sehen wollte.
     */
    onNavigiertZuSeite?: () => void;
    /** Kennung des Fensters, in dem die Leiste steckt — `null` im Lesepanel. */
    fensterId?: string | null;
    /**
     * Handy: keine Klickleiste am Rand — die Mail bekommt die volle Breite.
     * Die Reiter öffnet dann der Aufrufer (kontrolliert über `aktiverReiter`,
     * z. B. aus dem „…"-Menü der Vollansicht); das Overlay füllt die Breite.
     */
    ohneLeiste?: boolean;
};

export const MailContactSidebar: React.FC<MailContactSidebarProps> = ({
    contact,
    contactQuelle,
    readableMailboxes,
    mailAddresses,
    mailbox,
    email,
    aktiverReiter,
    onReiterWechsel,
    onOpenMail,
    onNavBack,
    onNavigiertZuSeite,
    fensterId,
    anhaenge,
    gespeicherteAnhangKeys,
    onOpenAnhang,
    onDownloadAnhang,
    onSaveAnhang,
    ablage,
    ohneLeiste = false,
}) => {
    const railW = ohneLeiste ? 0 : RAIL_W;
    const kontrolliert = aktiverReiter !== undefined;
    const [interner, setInterner] = React.useState<SidebarTabKey | null>(null);
    const activeTab = kontrolliert ? (aktiverReiter ?? null) : interner;
    const offen = activeTab !== null;
    const setActiveTab = React.useCallback(
        (t: SidebarTabKey | null) => {
            if (!kontrolliert) setInterner(t);
            onReiterWechsel?.(t);
        },
        [kontrolliert, onReiterWechsel],
    );

    const contactId = contact?.id ?? null;
    const noteMailbox = mailbox ?? null;
    const noteMessageId = email?.id ?? null;
    const tabs = React.useMemo(
        () => sichtbareTabs({ mailAdressierbar: false, dokumente: true }),
        [],
    );

    // Das Overlay bleibt nach dem ersten Öffnen im Baum (nur versteckt), damit
    // die geladenen Reiter-Daten ein Schließen überleben — sonst würde jedes
    // Wiederöffnen eine neue Graph-Suche auslösen.
    const [jeGeoeffnet, setJeGeoeffnet] = React.useState(false);
    React.useEffect(() => {
        if (activeTab !== null) setJeGeoeffnet(true);
    }, [activeTab]);

    // ESC schließt das Overlay. Der Listener hängt am Offen-Zustand statt am
    // konkreten Reiter — ein Reiterwechsel soll ihn nicht ab- und wieder anmelden.
    React.useEffect(() => {
        if (!offen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setActiveTab(null);
        };
        alleFenster.addEventListener("keydown", onKey);
        return () => alleFenster.removeEventListener("keydown", onKey);
    }, [offen, setActiveTab]);

    /* ── Styles ── */
    const railStyle: React.CSSProperties = {
        width: RAIL_W,
        minWidth: RAIL_W,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderLeft: `1px solid ${LINE}`,
        backgroundColor: BG_SOFT,
        zIndex: 4,
    };
    const railBtnStyle = (active: boolean): React.CSSProperties => ({
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        border: "none",
        borderLeft: active ? `3px solid ${GOLD}` : "3px solid transparent",
        background: active ? ACTIVE_BG : "transparent",
        color: active ? AUB : INK2,
        cursor: "pointer",
        padding: "10px 0",
        fontFamily: "inherit",
    });
    const railLabelStyle: React.CSSProperties = {
        writingMode: "vertical-rl",
        transform: "rotate(180deg)",
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.2px",
        whiteSpace: "nowrap",
    };
    const backdropStyle: React.CSSProperties = {
        position: "absolute",
        top: 0,
        left: 0,
        right: railW,
        bottom: 0,
        backgroundColor: "rgba(20,8,20,0.18)",
        zIndex: 5,
    };
    const panelStyle: React.CSSProperties = {
        position: "absolute",
        top: 0,
        right: railW,
        bottom: 0,
        width: ohneLeiste ? "100%" : `min(${PANEL_W}px, calc(100% - ${RAIL_W}px))`,
        backgroundColor: "#ffffff",
        borderLeft: `1px solid ${LINE}`,
        boxShadow: "-10px 0 28px rgba(20,8,20,0.16)",
        // Sichtbarkeit MUSS hier entschieden werden, nicht allein über das
        // hidden-Attribut: hidden wirkt nur über `display: none` aus dem
        // Browser-Stylesheet, und ein Inline-`display: flex` gewinnt gegen
        // jede Stylesheet-Regel. Genau daran ließ sich die Leiste, einmal
        // geöffnet, nicht mehr schließen — sie blieb sichtbar, egal was
        // activeTab sagte. Wer hier wieder ein festes "flex" einträgt, baut
        // den Fehler erneut.
        display: offen ? "flex" : "none",
        flexDirection: "column",
        zIndex: 6,
    };
    const panelHeadStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        padding: "10px 12px",
        borderBottom: `1px solid ${LINE}`,
        backgroundColor: BG_SOFT,
        flexShrink: 0,
    };
    const tabRowStyle: React.CSSProperties = {
        display: "flex",
        borderBottom: `1px solid ${LINE}`,
        flexShrink: 0,
        // Bei fünf Reitern in einem schmalen Fenster lieber scrollen als quetschen.
        overflowX: "auto",
    };
    const tabBtnStyle = (active: boolean): React.CSSProperties => ({
        flex: "1 0 auto",
        minWidth: 0,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "9px 4px",
        border: "none",
        borderBottom: active ? `2px solid ${GOLD}` : "2px solid transparent",
        background: "transparent",
        color: active ? AUB : INK2,
        fontWeight: active ? 700 : 500,
        fontSize: 12.5,
        cursor: "pointer",
        fontFamily: "inherit",
    });
    const bodyStyle: React.CSSProperties = {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: "10px 12px",
        backgroundColor: "#ffffff",
    };

    const closeOverlay = React.useCallback(() => setActiveTab(null), [setActiveTab]);
    /** Nach einer Navigation aus einem Reiter: Overlay zu — und Fenster aus dem Weg. */
    const nachNavigation = React.useCallback(() => {
        closeOverlay();
        onNavigiertZuSeite?.();
    }, [closeOverlay, onNavigiertZuSeite]);

    // Titel des Overlays: Kontaktname, sonst die Adresse der offenen Mail (die
    // Historie funktioniert auch ohne KMVS-Zuordnung).
    const ersteAdresse = (mailAddresses ?? []).map((a) => String(a ?? "").trim()).find(Boolean);
    const overlayTitel = contact?.displayName ?? ersteAdresse ?? "Kontakt-Historie";
    // Erklärt in einem Wort, warum dieser Kontakt oben steht.
    const quelleLabel =
        contactQuelle === "explizit" || contactQuelle === "link"
            ? "zugewiesen"
            : contactQuelle === "vorgang"
              ? "über den Vorgang"
              : contactQuelle === "erkannt"
                ? "erkannt"
                : null;

    return (
        <>
            {/* Schmale Klickleiste ganz rechts (immer sichtbar, solange eine Mail gelesen wird). */}
            {!ohneLeiste && (
            <div style={railStyle} aria-label="Kontakt-Sidebar">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        title={tab.label}
                        aria-label={tab.label}
                        style={railBtnStyle(activeTab === tab.key)}
                        onClick={() => setActiveTab(activeTab === tab.key ? null : tab.key)}
                    >
                        {TAB_ICONS[tab.key]}
                        <span style={railLabelStyle}>{tab.label}</span>
                    </button>
                ))}
            </div>
            )}

            {/* Overlay: Außenklick + ESC schließen. */}
            {offen && <div style={backdropStyle} onClick={closeOverlay} aria-hidden />}
            {jeGeoeffnet && (
                <div
                    style={panelStyle}
                    hidden={!offen}
                    /* Geschlossen ist das Panel nur noch Datenspeicher: `display: none`
                       nimmt es aus Layout, Fokusreihenfolge und Barrierefreiheits-Baum.
                       role/aria-label entfallen dann bewusst — ein „Dialog", den niemand
                       sehen oder erreichen kann, gehört Screenreadern nicht angesagt. */
                    role={offen ? "dialog" : undefined}
                    aria-hidden={offen ? undefined : true}
                    aria-label={
                        offen
                            ? `${overlayTitel} — ${tabs.find((t) => t.key === activeTab)?.label ?? ""}`
                            : undefined
                    }
                >
                    <div style={panelHeadStyle}>
                        <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            {onNavBack ? (
                                <button
                                    type="button"
                                    title="Zurück zur vorherigen Mail"
                                    aria-label="Zurück zur vorherigen Mail"
                                    onClick={onNavBack}
                                    style={{
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        color: AUB,
                                        display: "inline-flex",
                                        padding: 0,
                                        flexShrink: 0,
                                    }}
                                >
                                    <ChevronLeft20Regular />
                                </button>
                            ) : (
                                <ChevronLeft20Regular style={{ color: INK2, flexShrink: 0 }} />
                            )}
                            <span
                                style={{
                                    fontWeight: 700,
                                    fontSize: 13.5,
                                    color: AUB,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {overlayTitel}
                            </span>
                            {contact && quelleLabel && (
                                <span style={{ fontSize: 11, color: INK2, flexShrink: 0 }}>{quelleLabel}</span>
                            )}
                        </span>
                        <button
                            type="button"
                            title="Schließen"
                            aria-label="Sidebar schließen"
                            onClick={closeOverlay}
                            style={{
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                color: INK2,
                                display: "inline-flex",
                                padding: 2,
                            }}
                        >
                            <Dismiss20Regular />
                        </button>
                    </div>
                    <div style={tabRowStyle}>
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                title={tab.label}
                                style={tabBtnStyle(activeTab === tab.key)}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                <span
                                    style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        minWidth: 0,
                                    }}
                                >
                                    {tab.label}
                                </span>
                            </button>
                        ))}
                    </div>
                    <div style={bodyStyle}>
                        {/* Alle Reiter bleiben montiert (inaktive nur ausgeblendet):
                            so überlebt der geladene Inhalt einen Reiter-Wechsel,
                            geladen wird aber erst, wenn ein Reiter aktiv wird. */}
                        <div style={{ display: activeTab === "schriftwechsel" ? "block" : "none" }}>
                            <SchriftwechselTab
                                aktiv={activeTab === "schriftwechsel"}
                                contactId={contactId}
                                contactName={contact?.displayName ?? null}
                                mailAddresses={mailAddresses}
                                readableMailboxes={readableMailboxes}
                                onOpenMail={onOpenMail}
                                offeneMail={
                                    noteMailbox && noteMessageId
                                        ? { mailbox: noteMailbox, messageId: noteMessageId }
                                        : null
                                }
                            />
                        </div>
                        <div style={{ display: activeTab === "termine" ? "block" : "none" }}>
                            <TermineTab
                                aktiv={activeTab === "termine"}
                                contactId={contactId}
                                contactName={contact?.displayName ?? null}
                                mailAddresses={mailAddresses}
                                mailbox={noteMailbox}
                                email={email ?? null}
                                fensterId={fensterId}
                            />
                        </div>
                        <div style={{ display: activeTab === "vorgaenge" ? "block" : "none" }}>
                            <VorgaengeTab
                                aktiv={activeTab === "vorgaenge"}
                                contactId={contactId}
                                contactName={contact?.displayName ?? null}
                                onNavigated={nachNavigation}
                            />
                        </div>
                        <div style={{ display: activeTab === "dokumente" ? "block" : "none" }}>
                            <DokumenteTab
                                aktiv={activeTab === "dokumente"}
                                contactId={contactId}
                                contactName={contact?.displayName ?? null}
                                anhaenge={anhaenge}
                                gespeicherteKeys={gespeicherteAnhangKeys}
                                onOpenAnhang={onOpenAnhang}
                                onDownloadAnhang={onDownloadAnhang}
                                onSaveAnhang={onSaveAnhang}
                                ablage={ablage}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
